<template>
  <div class="w-full">
    <!-- Loading: same card chrome as sibling widgets -->
    <div v-if="loading" class="bg-white border border-gray-200 rounded-lg flex flex-col">
      <div class="px-4 py-3 bg-blue-50 border-b border-blue-100 rounded-t-lg">
        <h3 class="font-medium text-blue-900 text-sm">Insights</h3>
      </div>
      <div class="flex items-center justify-center h-[200px]">
        <div class="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12"></div>
      </div>
    </div>

    <div v-else-if="insights.length" class="grid grid-cols-2 gap-4 items-stretch">
      <!-- One card per insight, mirroring PerformanceTrend/PerformanceCohort chrome -->
      <div
        v-for="ins in insights"
        :key="ins.id"
        :class="ins.layout && ins.layout.width === 'half' ? 'col-span-1' : 'col-span-2'"
        class="bg-white border border-gray-200 rounded-lg flex flex-col h-[420px]"
      >
        <div class="px-4 py-3 bg-blue-50 border-b border-blue-100 rounded-t-lg flex items-center justify-between gap-3">
          <h3 class="font-medium text-blue-900 text-sm">{{ insightTitle(ins) }}</h3>

          <!-- Inline option toggle (e.g. Week | Month), data-driven from viz_config.options -->
          <div
            v-if="ins.viz_config && ins.viz_config.options"
            class="inline-flex rounded-md border border-blue-200 overflow-hidden text-xs shrink-0"
          >
            <button
              v-for="choice in ins.viz_config.options.choices"
              :key="choice.value"
              @click="selectOption(ins, choice)"
              :disabled="ins.optionLoading"
              :class="[
                'px-2.5 py-1 font-medium transition-colors',
                ins.selectedOption === choice.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-blue-700 hover:bg-blue-100',
              ]"
            >{{ choice.label }}</button>
          </div>
        </div>

        <!-- Card has a fixed height (h-[420px]); this body flex-fills the space
             below the header and the viz fills the body. The card header already
             shows the title, so viz components don't draw their own (see adapter). -->
        <div class="flex-1 min-h-0 p-4 relative">
          <div
            v-if="ins.optionLoading"
            class="absolute inset-0 bg-white/60 flex items-center justify-center z-10"
          >
            <div class="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-8 w-8"></div>
          </div>
          <div v-if="ins.error" class="flex items-center justify-center h-full text-gray-400 text-sm italic">
            Could not load this insight.
          </div>

          <Chart_V6
            v-else-if="ins.viz.component === 'chart_v6'"
            :spec="ins.viz.props.spec"
          />

          <D3Insight
            v-else-if="ins.viz.component === 'd3'"
            :code="ins.viz.props.code"
            :rows="ins.viz.props.rows"
            :min-height="ins.viz.props.minHeight"
          />
          <div v-else-if="ins.viz.component === 'chart_v5'" class="h-full">
            <Chart_V5
              :chart_data="ins.viz.props.chart_data"
              :chart_config="ins.viz.props.chart_config"
              override-height="h-full"
            />
          </div>

          <div v-else-if="ins.viz.component === 'table'" data-private class="h-full">
            <AgGridVue
              :theme="standardListTheme"
              style="width: 100%; height: 100%"
              :column-defs="ins.viz.props.columnDefs"
              :default-col-def="defaultColDef"
              :row-data="ins.viz.props.rowData"
            />
          </div>

          <Pie_Chart
            v-else-if="ins.viz.component === 'pie'"
            class="h-full"
            override-height="h-full"
            :chart_data="ins.viz.props.chart_data"
            :chart_config="ins.viz.props.chart_config"
          />

          <Sankey_Diagram
            v-else-if="ins.viz.component === 'sankey'"
            :chart-data="ins.viz.props.chartData"
            :config="ins.viz.props.config"
          />

          <HexMap
            v-else-if="ins.viz.component === 'heatmap'"
            :chart-data="ins.viz.props.chartData"
            :chart-config="ins.viz.props.chartConfig"
          />
        </div>
      </div>
    </div>

    <!-- Empty: same card chrome -->
    <div v-else class="bg-white border border-gray-200 rounded-lg flex flex-col">
      <div class="px-4 py-3 bg-blue-50 border-b border-blue-100 rounded-t-lg">
        <h3 class="font-medium text-blue-900 text-sm">Insights</h3>
      </div>
      <div class="flex items-center justify-center h-[120px] text-gray-400 text-sm italic">
        No insights configured for this metric.
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { AgGridVue } from 'ag-grid-vue3'
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue'
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue'
import Sankey_Diagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue'
import HexMap from '@/components/widgetcomponents/Viz_Elements/HexMap.vue'
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue'
import Spinning_Loader from '@/components/widgetcomponents/Viz_Elements/Spinning_Loader.vue'
import { standardListTheme } from '@/utils/common'
import { buildVizProps } from '@/utils/metricInsightAdapter'
import D3Insight from '@/components/Beta/D3Insight.vue'

export default {
  name: 'MetricInsightsWidget',
  components: { AgGridVue, Chart_V5, Chart_V6, Sankey_Diagram, HexMap, Pie_Chart, Spinning_Loader, D3Insight },
  props: {
    metricId: { type: Number, required: true },
    // matches InsightsNew_MetricView pageContext.year
    year: { type: Number, required: false, default: null },
    // when set, scopes each insight to a single entity (cte_final.ENTITY_ID)
    entityId: { type: Number, required: false, default: null },
  },
  data() {
    return {
      loading: false,
      insights: [],
      defaultColDef: { sortable: true, resizable: true, filter: true },
    }
  },
  computed: {
    standardListTheme() {
      return standardListTheme
    },
  },
  methods: {
    insightTitle(ins) {
      // The insight's name IS the display title (human-authored, mixed case).
      // Title-case any legacy SNAKE_CASE names as a fallback.
      const n = ins.name || ''
      if (/^[A-Z0-9_]+$/.test(n)) {
        return n.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      }
      return n
    },
    async load() {
      this.loading = true
      try {
        const params = { metric_id: this.metricId }
        if (this.year) params.observation_dt_year = this.year
        if (this.entityId) params.entity_id = this.entityId
        const { data } = await axios.get('api/metric-insights/with_data/', { params })
        this.insights = (data || []).map(ins => {
          const rows = Array.isArray(ins.data) ? ins.data : []
          const error = !Array.isArray(ins.data)
          const opts = ins.viz_config && ins.viz_config.options
          return {
            ...ins,
            error,
            viz: error ? null : buildVizProps(ins.viz_type, ins.viz_config, rows),
            selectedOption: opts ? opts.default : null,
            optionLoading: false,
          }
        })
      } catch (e) {
        console.error('Failed to load metric insights', e)
        this.insights = []
      } finally {
        this.loading = false
      }
    },
    async selectOption(ins, choice) {
      if (ins.selectedOption === choice.value || ins.optionLoading) return
      ins.optionLoading = true
      try {
        const payload = {
          metric_id: ins.metric_id || this.metricId,
          metric_insight_id: ins.insight_id,
          insight_type: ins.insight_type,
          sql_filters: { ...(choice.values || {}) },
        }
        if (this.year) payload.observation_dt_year = this.year
        if (this.entityId) payload.sql_filters.entity_id = this.entityId
        const { data } = await axios.post('api/returnmetricinsight/', payload)
        const rows = Array.isArray(data) ? data : []
        ins.error = !Array.isArray(data)
        ins.viz = ins.error ? null : buildVizProps(ins.viz_type, ins.viz_config, rows)
        ins.selectedOption = choice.value
      } catch (e) {
        console.error('Failed to switch insight option', e)
        ins.error = true
      } finally {
        ins.optionLoading = false
      }
    },
  },
  watch: {
    metricId() {
      this.load()
    },
    year() {
      this.load()
    },
    entityId() {
      this.load()
    },
  },
  mounted() {
    this.load()
  },
}
</script>

<style scoped></style>
